import { getGlobal } from '../global';
import { isEmail as isEmailHandler } from './';
/**
 * Validation the string is an valid email
 * @param {String} str The string to check an email
 * @param {String} message the error will return if str is not an email
 * @returns {Boolean} true if value is an email, message or default error if value is not an email
 */

export const isEmail = (str, _, message) => {
  if (!nonNullish(str) || str === '') return true;
  return isEmailHandler(str) || (nonNullish(message) ? message : getGlobal(['validate', 'isEmail']));
};
/**
 * Validation value is not an empty string, array or object
 * @param {String|Object|Array} value the value to validate not empty
 * @param {String} message the error will return if value is empty
 * @returns {Boolean} true if value is not empty, message or default error if value is empty
 */

export const isNotEmpty = (value, _, message) => {
  if (!nonNullish(value)) return getGlobal(['validate', 'invalidInput']);

  if (typeof value === 'string') {
    return value !== '' || message || getGlobal(['validate', 'isNotEmptyString']);
  } else if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length || message || getGlobal(['validate', 'isNotEmptyArray']);
    } else {
      return Object.keys(value).length || message || getGlobal(['validate', 'isNotEmptyObject']);
    }
  }

  return true;
};
/**
 * Check a number is less than or equal the maximum allowed
 * @param {Number} value the value to compare with maximum
 * @param {Number} maximum the maximum value
 * @param {String} message the error will return if value > maximum
 * @returns {Boolean|String} true if value is valid, message or default error if invalid (both value and maximum)
 */

export const max = (value, maximum, message) => {
  if (!nonNullish([value, maximum]) || value === '') return true;
  if (isNaN(value) || isNaN(maximum)) return getGlobal(['validate', 'invalidInput']);
  return value <= maximum || (nonNullish(message) ? message : `${getGlobal(['validate', 'max'])} (${maximum})`);
};
/**
 * check a number is greater than or equal the minimum allowed
 * @param {Number} value the value to compare with minimize
 * @param {Number} minimize the minimize value
 * @param {String} message the error will return if value < minimize
 * @returns {Boolean|String} true if value is valid, message or default error if invalid (both value and minimize)
 */

export const min = (value, minimize, message) => {
  if (!nonNullish([value, minimize]) || value === '') return true;
  if (isNaN(value) || isNaN(minimize)) return getGlobal(['validate', 'invalidInput']);
  return value >= minimize || (nonNullish(message) ? message : `${getGlobal(['validate', 'min'])} (${minimize})`);
};
/**
 * check value has anything
 * @param {String|Number|Object} value the value to check
 * @param {String} message the error will return if undefined|null|empty
 * @returns {Boolean|String} true if value is valid, message input or default error if value invalid
 */

export const required = (value, message) => {
  return nonNullish(value) && value !== '' || (nonNullish(message) ? message : getGlobal(['validate', 'required']));
};

const nonNullish = value => {
  if (Array.isArray(value)) {
    return value.every(v => v !== undefined && v !== null);
  }

  return value !== undefined && value !== null;
};

const validations = {
  isEmail,
  isNotEmpty,
  max,
  min,
  required
};
/**
 * validation value follow rule(s)
 * @param {String|Number|Object|Array} value the value to validate
 * @param {String|Object|Array} rules the rule(s) to validation value
 * @param {Boolean} returnOneError only return first error if have errors
 * @returns {Boolean|String|Array} true if value is valid, error text or an array error if invalid value
 */

const onValidate = (value, rules, returnOneError) => {
  if (!rules) return true;
  let result = true;

  if (typeof rules === 'function') {
    result = rules(value);
  } else if (typeof rules === 'object') {
    if (Array.isArray(rules)) {
      rules.forEach(rule => {
        let res;

        if (typeof rule === 'string') {
          res = validations[rule](value);
        } else {
          const name = rule.name || Object.keys(rule)[0];
          const compareValue = rule.compareValue !== undefined ? rule.compareValue : rule[name];

          if (name && validations[name]) {
            res = validations[name](value, compareValue, rule.message);
          }
        }

        if (res !== true) {
          if (result === true) {
            result = [];
          }

          result.push(res);
        }
      });
    } else {
      const name = rules.name || Object.keys(rules)[0];
      const compareValue = rules.compareValue !== undefined ? rules.compareValue : rules[name];

      if (name) {
        result = validations[name](value, compareValue, rules.message);
      }
    }
  } else if (validations[rules]) {
    result = validations[rules](value);
  }

  if (returnOneError) {
    if (result === true) {
      return false;
    } else if (typeof result === 'string') {
      return result;
    } else return result[0];
  }

  return result;
};

export default onValidate;